import { ArrowDown, ArrowUp, Download, LoaderCircle, RefreshCw, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, VaelorApi } from "../lib/api";
import {
  addTacticalPlannerStep,
  describeArmedOrderStatus,
  downloadTacticalPlannerFile,
  exportTacticalPlannerCsv,
  exportTacticalPlannerJson,
  removeTacticalPlannerStep,
  reorderTacticalPlannerSteps,
} from "../lib/tactical-planner";
import type { GameResources, GameSnapshot, TacticalPlannerResponse, TacticalPlannerStep } from "../types";

function resourceSummary(resources: GameResources): string {
  return [
    `bois ${resources.bois}`,
    `scorie ${resources.scorie}`,
    `sang ${resources.sang}`,
    `ignis ${resources.ignis}`,
  ].join(" · ");
}

function plannerErrorMessage(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  return "La Table de Commandement est momentanément indisponible.";
}

function newIntentId(): string {
  return `intent-${crypto.randomUUID().replaceAll("-", "").slice(0, 32)}`;
}

export function TacticalPlanner({
  api,
  onClose,
}: {
  api: VaelorApi;
  onClose: () => void;
}) {
  const [response, setResponse] = useState<TacticalPlannerResponse>();
  const [gameSnapshot, setGameSnapshot] = useState<GameSnapshot>();
  const [draftSteps, setDraftSteps] = useState<TacticalPlannerStep[]>([]);
  const [selectedBuilding, setSelectedBuilding] = useState("");
  const [orderKind, setOrderKind] = useState<"construction" | "research">("construction");
  const [orderTarget, setOrderTarget] = useState("");
  const [loading, setLoading] = useState(true);
  const [reloading, setReloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();

  const loadPlan = useCallback(async (manual = false) => {
    if (manual) setReloading(true);
    else setLoading(true);
    setError(undefined);
    try {
      const next = await api.constructionPlan();
      setResponse(next);
      setDraftSteps(next.steps.map((step) => ({ ...step })));
      setSelectedBuilding(next.buildingKeys[0] ?? "");
      setOrderTarget(next.buildingKeys[0] ?? "");
      try {
        const bootstrap = await api.gameBootstrap();
        setGameSnapshot(bootstrap.snapshot);
      } catch {
        setGameSnapshot(undefined);
      }
    } catch (cause) {
      setError(plannerErrorMessage(cause));
    } finally {
      if (manual) setReloading(false);
      else setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void loadPlan();
  }, [loadPlan]);

  const mutationDisabled = saving || response?.entitlement.status !== "active";
  const projectionByIntent = useMemo(
    () => new Map(response?.projection.steps.map((step) => [step.intentId, step]) ?? []),
    [response],
  );
  const draftChanged = response
    ? JSON.stringify(draftSteps) !== JSON.stringify(response.steps)
    : false;
  const authoritativeState = gameSnapshot?.initialized ? gameSnapshot.state : undefined;
  const researchKeys = Object.keys(authoritativeState?.researchTree ?? {}).sort();
  const orderTargets = orderKind === "construction"
    ? response?.buildingKeys ?? []
    : researchKeys;
  const armedOrder = authoritativeState?.armedOrder ?? null;
  const armedOrderTerminal = !armedOrder || [
    "started",
    "expired",
    "stale",
    "stale_generation",
    "stale_rules",
    "obsolete",
    "cancelled",
  ].includes(armedOrder.status);

  const addStep = () => {
    if (mutationDisabled || !selectedBuilding) return;
    setDraftSteps((current) => addTacticalPlannerStep(current, selectedBuilding, newIntentId()));
    setNotice(undefined);
  };

  const savePlan = async () => {
    if (!response || mutationDisabled) return;
    setSaving(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const next = await api.replaceConstructionPlan(response.revision, draftSteps);
      setResponse(next);
      setDraftSteps(next.steps.map((step) => ({ ...step })));
      setSelectedBuilding(next.buildingKeys[0] ?? "");
      setNotice("Le plan privé a été enregistré et recalculé depuis le dernier snapshot.");
    } catch (cause) {
      setError(plannerErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const armOrder = async () => {
    if (!response || response.entitlement.status !== "active" || !orderTarget) return;
    setSaving(true);
    setError(undefined);
    setNotice(undefined);
    try {
      const result = await api.armOrder(orderKind, orderTarget);
      setGameSnapshot(result.snapshot);
      setNotice("Ordre armé. Aucune ressource n’est réservée ; le serveur revalidera chaque étape.");
    } catch (cause) {
      setError(plannerErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const cancelOrder = async () => {
    if (!armedOrder) return;
    setSaving(true);
    setError(undefined);
    try {
      const result = await api.cancelOrder(armedOrder.orderId);
      setGameSnapshot(result.snapshot);
      setNotice("Ordre annulé. Aucun remboursement n’était nécessaire : aucune ressource n’était réservée.");
    } catch (cause) {
      setError(plannerErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const manualStartOrder = async () => {
    if (!armedOrder || armedOrder.mode !== "legacy_manual") return;
    setSaving(true);
    setError(undefined);
    try {
      const result = await api.manualStartOrder(armedOrder.orderId);
      setGameSnapshot(result.snapshot);
      setNotice("Démarrage manuel accepté par le serveur.");
    } catch (cause) {
      setError(plannerErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const exportJson = () => {
    if (!response) return;
    downloadTacticalPlannerFile(
      exportTacticalPlannerJson(response),
      "vaelor-planificateur-du-bastion.json",
      "application/json;charset=utf-8",
    );
  };

  const exportCsv = () => {
    if (!response) return;
    downloadTacticalPlannerFile(
      exportTacticalPlannerCsv(response),
      "vaelor-planificateur-du-bastion.csv",
      "text/csv;charset=utf-8",
    );
  };

  return (
    <aside className="tactical-planner" aria-labelledby="tactical-planner-title">
      <header className="tactical-planner__header">
        <div>
          <p className="eyebrow">Table de Commandement</p>
          <h2 id="tactical-planner-title">Planificateur du Bastion</h2>
        </div>
        <button className="icon-command" type="button" onClick={onClose} aria-label="Fermer la Table de Commandement">
          <X size={18} aria-hidden="true" />
        </button>
      </header>

      <p className="tactical-planner__promise">
        La base gratuite reste complète et manuelle. Le Pacte du Sénéchal peut armer un seul ordre construction ou recherche, sans réservation de ressources, avec une validation serveur à chaque réveil.
      </p>

      {loading ? (
        <div className="notice" role="status"><LoaderCircle className="spin" size={17} /> Lecture du plan…</div>
      ) : error && !response ? (
        <div className="notice notice--error" role="alert">
          <span>{error}</span>
          <button className="secondary-command" type="button" onClick={() => void loadPlan(true)} disabled={reloading}>
            <RefreshCw size={15} aria-hidden="true" /> Relire
          </button>
        </div>
      ) : response ? (
        <>
          <div className={`tactical-planner__entitlement tactical-planner__entitlement--${response.entitlement.status}`}>
            <strong>{response.entitlement.status === "active" ? "Droit actif" : "Droit inactif"}</strong>
            <span>
              {response.entitlement.status === "active"
                ? response.entitlement.endsAt
                  ? `Jusqu’au ${new Date(response.entitlement.endsAt).toLocaleString("fr-FR")}.`
                  : "Aucune date de fin fournie."
                : "La lecture et les exports restent disponibles ; les changements sont désactivés."}
            </span>
          </div>

          <section className="command-order" aria-labelledby="command-order-title">
            <div className="command-order__heading">
              <div>
                <p className="eyebrow">Capacité globale 1</p>
                <h3 id="command-order-title">Ordre de relève</h3>
              </div>
              <span className="command-order__capacity">Construction XOR recherche</span>
            </div>
            <p className="command-order__copy">
              Une activité à la fois. Les ressources ne sont jamais réservées par un ordre ; les coûts, durées et prérequis sont recalculés au lancement.
            </p>
            <div className="command-order__free" role="status">
              <strong>Gratuit</strong>
              <span>
                {authoritativeState?.construction
                  ? "Construction active · le prochain démarrage reste manuel."
                  : authoritativeState?.researchJob
                    ? "Recherche active · le prochain démarrage reste manuel."
                    : "Démarrage manuel requis ; aucune ressource réservée."}
              </span>
            </div>
            {armedOrder && (
              <div className={`command-order__armed command-order__armed--${armedOrder.status}`} role="status" aria-live="polite">
                <div>
                  <strong>{armedOrder.mode === "premium_auto" ? "Premium" : "Legacy manuel"} · {armedOrder.kind}</strong>
                  <span>{armedOrder.targetKey} · {describeArmedOrderStatus(armedOrder.status)}</span>
                  {armedOrder.statusReason && <small>Motif : {armedOrder.statusReason}</small>}
                  <small>Aucune ressource réservée · ordre {armedOrder.orderId}</small>
                </div>
                <div className="command-order__armed-actions">
                  {armedOrder.mode === "legacy_manual" && !armedOrderTerminal && (
                    <button
                      className="secondary-command"
                      type="button"
                      onClick={() => void manualStartOrder()}
                      disabled={saving || Boolean(authoritativeState?.construction || authoritativeState?.researchJob)}
                    >
                      Démarrer manuellement
                    </button>
                  )}
                  {!armedOrderTerminal && (
                    <button className="icon-command" type="button" onClick={() => void cancelOrder()} disabled={saving} aria-label="Annuler l’ordre armé">
                      <X size={16} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="command-order__arm-form">
              <label htmlFor="command-order-kind">Type de cible</label>
              <select
                id="command-order-kind"
                value={orderKind}
                onChange={(event) => {
                  const nextKind = event.target.value as "construction" | "research";
                  setOrderKind(nextKind);
                  setOrderTarget(nextKind === "construction" ? response.buildingKeys[0] ?? "" : researchKeys[0] ?? "");
                }}
                disabled={saving || response.entitlement.status !== "active" || !armedOrderTerminal}
              >
                <option value="construction">Construction</option>
                <option value="research">Recherche</option>
              </select>
              <label htmlFor="command-order-target">Cible</label>
              <select
                id="command-order-target"
                value={orderTarget}
                onChange={(event) => setOrderTarget(event.target.value)}
                disabled={saving || response.entitlement.status !== "active" || !armedOrderTerminal || orderTargets.length === 0}
              >
                {orderTargets.length === 0
                  ? <option value="">Aucune cible disponible</option>
                  : orderTargets.map((target) => <option key={target} value={target}>{target}</option>)}
              </select>
              <button
                className="primary-command"
                type="button"
                onClick={() => void armOrder()}
                disabled={saving || response.entitlement.status !== "active" || !armedOrderTerminal || !orderTarget}
              >
                {saving ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : null}
                {armedOrder ? "Réarmer explicitement" : "Armer l’ordre premium"}
              </button>
            </div>
            <small className="command-order__risk">
              Le confort d’occupation du slot pendant une absence peut être perçu comme un avantage P2W ; il ne change ni coût, ni durée, ni résultat, ni capacité.
            </small>
          </section>

          {error && (
            <div className="notice notice--error" role="alert">
              <span>{error}</span>
              <button className="secondary-command" type="button" onClick={() => void loadPlan(true)} disabled={reloading}>
                <RefreshCw size={15} aria-hidden="true" /> Relire
              </button>
            </div>
          )}
          {notice && <div className="notice notice--success" role="status">{notice}</div>}

          <div className="tactical-planner__actions" aria-label="Exports du plan">
            <button className="secondary-command" type="button" onClick={exportJson}>
              <Download size={15} aria-hidden="true" /> JSON
            </button>
            <button className="secondary-command" type="button" onClick={exportCsv}>
              <Download size={15} aria-hidden="true" /> CSV
            </button>
          </div>

          <div className="tactical-planner__source">
            <span>Révision {response.revision}</span>
            <span>Économie {response.projection.economyVersion}</span>
            <span>Snapshot {response.projection.sourceStateVersion} · {new Date(response.projection.sourceUpdatedAt).toLocaleString("fr-FR")}</span>
          </div>
          {draftChanged && (
            <div className="notice" role="status">
              L’ordre local n’est pas encore enregistré. Les coûts, durées et prérequis seront recalculés après l’enregistrement.
            </div>
          )}

          {response.entitlement.status === "active" && (
            <div className="tactical-planner__add-row">
              <label htmlFor="tactical-planner-building">Ajouter une intention</label>
              <div>
                <select
                  id="tactical-planner-building"
                  value={selectedBuilding}
                  onChange={(event) => setSelectedBuilding(event.target.value)}
                  disabled={mutationDisabled || response.buildingKeys.length === 0}
                >
                  {response.buildingKeys.map((buildingKey) => (
                    <option key={buildingKey} value={buildingKey}>{buildingKey}</option>
                  ))}
                </select>
                <button className="secondary-command" type="button" onClick={addStep} disabled={mutationDisabled || draftSteps.length >= 50 || !selectedBuilding}>
                  Ajouter
                </button>
              </div>
            </div>
          )}

          {draftSteps.length === 0 ? (
            <p className="tactical-planner__empty">Aucune intention. Le plan peut rester vide et sera conservé.</p>
          ) : (
            <ol className="tactical-planner__steps">
              {draftSteps.map((step, index) => {
                const projection = draftChanged ? undefined : projectionByIntent.get(step.intentId);
                return (
                  <li key={step.intentId} className="tactical-planner__step">
                    <div className="tactical-planner__step-main">
                      <span className="tactical-planner__step-number" aria-hidden="true">{index + 1}</span>
                      <div>
                        <strong>{step.buildingKey}</strong>
                        <small>{projection ? `${projection.fromLevel} → ${projection.toLevel} · ${projection.durationSeconds}s · ETA ${new Date(projection.eta).toLocaleString("fr-FR")}` : "Projection à recalculer après enregistrement."}</small>
                        {projection && <>
                          <small>Coût : {resourceSummary(projection.cost)} · Déficit cumulé : {resourceSummary(projection.deficits)}</small>
                          <small>Prérequis : {projection.prerequisites.status === "met" ? "satisfaits" : "à vérifier"}</small>
                        </>}
                      </div>
                    </div>
                    <div className="tactical-planner__step-actions" aria-label={`Actions pour ${step.buildingKey}`}>
                      <button className="icon-command" type="button" onClick={() => setDraftSteps((current) => reorderTacticalPlannerSteps(current, index, -1))} disabled={mutationDisabled || index === 0} aria-label="Monter l’intention" title="Monter">
                        <ArrowUp size={16} aria-hidden="true" />
                      </button>
                      <button className="icon-command" type="button" onClick={() => setDraftSteps((current) => reorderTacticalPlannerSteps(current, index, 1))} disabled={mutationDisabled || index === draftSteps.length - 1} aria-label="Descendre l’intention" title="Descendre">
                        <ArrowDown size={16} aria-hidden="true" />
                      </button>
                      <button className="icon-command" type="button" onClick={() => setDraftSteps((current) => removeTacticalPlannerStep(current, index))} disabled={mutationDisabled} aria-label="Retirer l’intention" title="Retirer">
                        <X size={16} aria-hidden="true" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}

          <div className="tactical-planner__footer-actions">
            <button className="primary-command" type="button" onClick={() => void savePlan()} disabled={mutationDisabled || !draftChanged}>
              {saving ? <LoaderCircle className="spin" size={16} aria-hidden="true" /> : null}
              Enregistrer l’ordre
            </button>
            <span>{draftSteps.length}/50 intentions</span>
          </div>
        </>
      ) : null}
    </aside>
  );
}
